/* eslint-env browser */
import Vue from "vue";
import Vuex from "vuex";
import axios from "axios";

import { GLOBALS } from "../js/helpers";

Vue.use(Vuex);

const store = new Vuex.Store({
  state: {
    config: {
      ...GLOBALS.config,
      termsBound: false
    },

    user: {
      ...GLOBALS.user
    },
    sitecore: {
      ...GLOBALS.sitecore
    },
    games: {
      ...GLOBALS.games
    },

    labels: {},
    promo: {
      isLive: true
    },

    game: {
      winner: false,
      loser: false,
      alreadyPlayed: false,
      prize: null
    },

    views: {
      game: {
        id: 0,
        title: "CoinFlip"
      },
      active: 0
    },
    loading: true
  },

  getters: {
    activeView(state, getters) {
      const views = getters.viewItems;
      return {
        id: state.views.active,
        title: views[state.views.active].title,
        title_second: views[state.views.active].title_second,
        sub: views[state.views.active].subtitle,
        intro: views[state.views.active].intro,
        btn_login: views[state.views.active].btn_login,
        btn_register: views[state.views.active].btn_register,
        btn_yellow: views[state.views.active].btn_yellow,
        btn_black: views[state.views.active].btn_black,
        raffle_label: views[state.views.active].raffle_label,
        significant_terms: views[state.views.active].significant_terms,
        lang: state.sitecore.lang
      };
    },
    getGamesData(state) {
      return state.games;
    },
    getAlreadyPlayed(state) {
      return state.game.alreadyPlayed;
    },
    getFAQs(state) {
      return state.labels.faqs.map((faq, index) => ({
        title: faq.question,
        content: faq.answer,
        id: index
      }));
    },
    getHost(state) {
      return state.config.host;
    },
    getLabels(state) {
      return state.labels;
    },
    getLanguage(state) {
      return state.sitecore.lang;
    },
    getPortal(state) {
      return state.sitecore.portal;
    },
    getPMP(state) {
      return state.user.isRealPlayer;
    },
    getTargetGroup(state) {
      return state.user.targetGroup;
    },
    getRaffleEntries(state) {
      return state.user.raffleEntries;
    },
    getUserID(state) {
      return state.user.customerId;
    },
    isLoading(state) {
      return state.loading;
    },
    isLoggedIn(state) {
      return state.user.loggedIn || state.user.isLoggedIn;
    },
    isLoser(state) {
      return state.game.loser;
    },
    getIsMobile(state) {
      return state.sitecore.isMobile;
    },
    getIsNative(state) {
      return state.sitecore.isNative;
    },
    isPromoAvailable(state, getters) {
      return state.promo.isLive && getters.isLoggedIn;
    },
    isWinner(state) {
      return state.game.winner;
    },
    viewItems(state) {
      return Object.keys(state.views)
        .filter(view => {
          return view !== "active";
        })
        .map(view => ({
          id: state.views[view].id,
          nav: state.views[view].nav,
          title: state.views[view].title,
          title_second: state.views[view].title_second,
          subtitle: state.views[view].sub || null,
          intro: state.views[view].intro || null,
          btn_login: state.views[view].btn_login,
          btn_register: state.views[view].btn_register,
          btn_yellow: state.views[view].btn_yellow,
          btn_black: state.views[view].btn_black,
          raffle_label: state.views[view].raffle_label,
          significant_terms: state.views[view].significant_terms
        }));
    },
    notificationMessages(state) {
      return state.labels.notifications;
    },
    requireUKRegs(state) {
      return state.sitecore.region === "uk";
    },
    getTermsBound(state) {
      return state.config.termsBound;
    }
  },

  mutations: {
    setActiveView(state, payload) {
      Vue.set(state.views, "active", payload);
    },
    setAlreadyPlayed(state) {
      Vue.set(state.game, "alreadyPlayed", true);
    },
    setEncoded(state, payload) {
      Vue.set(state.game, "encodedCards", payload);
    },
    setLabels(state, payload) {
      Vue.set(state, "labels", payload);
    },
    setLoading(state, payload) {
      Vue.set(state, "loading", payload);
    },
    setLoser(state) {
      Vue.set(state.game, "loser", true);
    },
    setTargetGroup(state, payload) {
      Vue.set(state.user, "targetGroup", payload);
    },
    setRaffleEntries(state, payload) {
      Vue.set(state.user, "raffleEntries", payload);
    },
    setRecentGames(state, payload) {
      Vue.set(state.game, "recentGames", payload);
    },
    setTermsBound(state) {
      Vue.set(state.config, "termsBound", true);
    },
    setView(state, payload) {
      Vue.set(state.views, `${payload.name}`, payload);
    },
    setWinner(state) {
      Vue.set(state.game, "winner", true);
    }
  },

  actions: {
    setLoading(context, bool) {
      context.commit("setLoading", bool);
    },
    setActiveView(context, payload) {
      context.commit("setActiveView", payload);
    },

    setRaffleModal(context) {
      const stateObj = context.state;
      const api = stateObj.config.api;
      const postData = {
        label: stateObj.sitecore.portal,
        lang: stateObj.sitecore.lang
      };

      dataLayer.push({
        event: "Event.Functionality.",
        "page.referringAction": "coin-flip_RaffleTiX_Popup_Shown"
      });

      const buildNotification = data => {
        const ctaUrl = GLOBALS.getCtaUrl(stateObj.sitecore, data);
        return Object.freeze({
          title: data.header,
          message: data.body,
          footer: data.footer,
          action: {
            text: data.cta_text,
            url: ctaUrl,
            native: ctaUrl
          },
          allow_close: data.allow_close,
          cta_show: data.cta_show
        });
      };

      return new Promise(resolve => {
        let notification = null;
        axios
          .get(
            `${api}/v2/CRM.svc/BonusMessage/raffle_cf?label=${
              postData.label
            }&language=${postData.lang}`
          )
          .then(genericRes => {
            notification = buildNotification(genericRes.data.data);
            resolve(notification);
          });
      });
    },

    setWinLose(context, payload) {
      const stateObj = context.state;
      const api = stateObj.config.api;
      let messageTypeTrackingValue = "LoserMessage";
      let channelType = "0";
      if (stateObj.sitecore.isNative === true) {
        channelType = "3";
      } else if (stateObj.sitecore.isMobile === true) {
        channelType = "1";
      } else {
        channelType = "2";
      }
      const postData = {
        userid: `${stateObj.user.customerId}`,
        label: stateObj.sitecore.portal,
        lang: stateObj.sitecore.lang,
        bonus_promotion_gid: stateObj.sitecore.bonusID,
        color: payload,
        channel: channelType,
        targetgroup_id: stateObj.sitecore.targetGroupID
      };

      const buildNotification = data => {
        const ctaUrl = GLOBALS.getCtaUrl(stateObj.sitecore, data);

        return Object.freeze({
          title: data.header,
          message: data.body,
          footer: data.footer,
          action: {
            text: data.cta_text,
            url: ctaUrl,
            native: ctaUrl
          },
          allow_close: data.allow_close,
          cta_show: data.cta_show,
          show_games: data.name,
          messageTypeTracking: messageTypeTrackingValue
        });
      };

      const headers = {
        "Content-Type": "application/json",
        "x-bwin-session-token": stateObj.user.sessionToken,
        "x-bwin-user-token": stateObj.user.userToken
      };
      return new Promise(resolve => {
        axios
          .post(
            `${api}/v1/sports/flipcoin/user/process`,
            JSON.stringify(postData),
            // eslint-disable-next-line object-shorthand
            { headers: headers }
          )
          .then(response => {
            const setPayload = () => {
              const animation = document.querySelector(".c-coin-animation");

              if (
                response.data.data !== null &&
                response.data.data.win === true
              ) {
                animation.classList.remove("start-animation");
                if (response.data.data.color.toString() === "0") {
                  animation.classList.add("flipblack");
                } else {
                  animation.classList.add("flipyellow");
                }
              } else {
                animation.classList.remove("start-animation");
                if (response.data.data.color.toString() === "0") {
                  animation.classList.add("flipyellow");
                } else {
                  animation.classList.add("flipblack");
                }
              }
            };

            const hasError = response.data.error;
            const hasBonus =
              response.data.data !== null && response.data.data.bonus !== null;

            let notification = null;

            if (hasBonus) {
              const bonus = response.data.data.bonus;

              let exitUrl = "";
              if (stateObj.sitecore.isNative === true) {
                exitUrl = bonus.exit_url_native;
              } else if (stateObj.sitecore.isMobile === true) {
                exitUrl = bonus.exit_url_mobile;
              } else {
                exitUrl = bonus.exit_url_desktop;
              }

              notification = Object.freeze({
                title: `${bonus.bonus_title}`,
                message: (() => {
                  const lines = [
                    bonus.bonus_text,
                    bonus.bonus_text_1,
                    bonus.bonus_text_2
                  ];

                  return lines
                    .map(line => {
                      let lineReturn = null;
                      if (line !== undefined && line !== null) {
                        lineReturn = `<p>${line}</p>`;
                      }

                      return lineReturn;
                    })
                    .join("");
                })(),
                footer: `${bonus.bonus_footer}`,
                action: {
                  text: `${bonus.exit_text}`,
                  url: `${exitUrl.replace(
                    /{SESSIONKEY}/g,
                    context.state.user.Sso
                  )}`,
                  native: `${exitUrl.replace(
                    /{SESSIONKEY}/g,
                    context.state.user.Sso
                  )}`
                },
                messageTypeTracking: "WinnerMessage",

                show_games: bonus.award_code
              });
              setPayload();
              console.log('bonus:'+ bonus.award_code);
              context.commit("setRaffleEntries", response.data.data.raffle);
              setTimeout(() => {
                resolve(notification);
              }, 4000);
              dataLayer.push({
                event: "Event.Functionality.",
                "page.referringAction": "coin-flip_WinnerMessage_Shown"
              });
            } else if (hasError) {
              if (hasError.code === "E001") {
                const notificationData = {
                  allow_close: 0,
                  body: hasError.message,
                  cta_show: 1,
                  cta_text: "Go to Lobby",
                  cta_url: "!!Casino",
                  cta_url_desktop: "https://casino.bwin.com/en/",
                  cta_url_mobile: "https://casino.m.bwin.com/en/casino/",
                  cta_url_native: "https://casino.m.bwin.com/en/casino/",
                  header: "Error E001"
                };

                notification = buildNotification(notificationData);
                resolve(notification);
              }
              if (
                hasError.code === "E002" ||
                hasError.code === "E004" ||
                hasError.code === "E005"
              ) {
                const notificationData = {
                  allow_close: 0,
                  body: hasError.message,
                  cta_show: 1,
                  cta_text: "Go to Lobby",
                  cta_url: "!!Casino",
                  cta_url_desktop: "https://casino.bwin.com/en/",
                  cta_url_mobile: "https://casino.m.bwin.com/en/casino/",
                  cta_url_native: "https://casino.m.bwin.com/en/casino/",
                  header: "Error " + hasError.code
                };

                notification = buildNotification(notificationData);
                resolve(notification);
              } else if (hasError.code === "E003") {
                axios
                  .get(
                    `${api}/v2/CRM.svc/BonusMessage/daily_cf?label=${
                      postData.label
                    }&language=${postData.lang}`
                  )
                  .then(genericRes => {
                    notification = buildNotification(genericRes.data.data);
                    resolve(notification);
                    dataLayer.push({
                      event: "Event.Functionality.",
                      "page.referringAction": "coin-flip_TryAgainTomorrow_Shown"
                    });
                  });
              }
              // else if (hasError.code === "E004" || hasError.code === "E005") {
              //   axios
              //     .get(
              //       `${api}/v2/CRM.svc/BonusMessage/generic?label=${
              //         postData.label
              //       }&language=${postData.lang}`
              //     )
              //     .then(genericRes => {
              //       messageTypeTrackingValue = "Generic_Error";
              //       notification = buildNotification(genericRes.data.data);
              //       resolve(notification);
              //       dataLayer.push({
              //         event: "Event.Functionality.",
              //         "page.referringAction": "coin-flip_Generic_Error"
              //       });
              //     });
              // }
              else if (hasError.code === "E006") {
                axios
                  .get(
                    `${api}/v2/CRM.svc/BonusMessage/res_122_cf?label=${
                      postData.label
                    }&language=${postData.lang}`
                  )
                  .then(genericRes => {
                    messageTypeTrackingValue = "DoubleBonus";
                    notification = buildNotification(genericRes.data.data);
                    resolve(notification);
                    dataLayer.push({
                      event: "Event.Functionality.",
                      "page.referringAction": "coin-flip_DoubleBonus_Error"
                    });
                  });
              }
            } else {
              axios
                .get(
                  `${api}/v2/CRM.svc/BonusMessage/loser_cf?label=${
                    postData.label
                  }&language=${postData.lang}`
                )
                .then(genericRes => {
                  setPayload();
                  notification = buildNotification(genericRes.data.data);
                  setTimeout(() => {
                    resolve(notification);
                  }, 4000);
                  dataLayer.push({
                    event: "Event.Functionality.",
                    "page.referringAction": "coin-flip_LoserMessage_Shown"
                  });
                });
            }
          });
      });
    },

    setTermsBound(context) {
      context.commit("setTermsBound");
    },

    loadAlreadyPlayedMessage(context) {
      const stateObj = context.state;
      const api = stateObj.config.api;
      const portal = stateObj.sitecore.portal;
      const langCode = stateObj.sitecore.langCode;
      let notification = null;

      const buildNotification = data => {
        console.log("====================================");
        console.log(data);
        console.log("====================================");
        const ctaUrl = GLOBALS.getCtaUrl(stateObj.sitecore, data);
        return Object.freeze({
          title: data.header,
          message: data.body,
          footer: data.footer,
          action: {
            text: data.cta_text,
            url: ctaUrl,
            native: ctaUrl
          },
          allow_close: data.allow_close,
          cta_show: data.cta_show,
          show_games: "daily_cf"
        });
      };

      return new Promise(resolve => {
        axios
          .get(
            `${api}/v2/CRM.svc/BonusMessage/daily_cf?label=${portal}&language=${langCode}`
          )
          .then(errorRes => {
            if (errorRes && errorRes !== undefined) {
              notification = buildNotification(errorRes.data.data);
            }
            resolve(notification);
            dataLayer.push({
              event: "Event.Functionality.",
              "page.referringAction": "coin-flip_TryAgainTomorrow_Shown"
            });
          });
      });
    },

    loadRealMoneyPlayerdMessage(context) {
      const stateObj = context.state;
      const api = stateObj.config.api;
      const portal = stateObj.sitecore.portal;
      const langCode = stateObj.sitecore.langCode;
      let notification = null;

      const buildNotification = data => {
        const ctaUrl = GLOBALS.getCtaUrl(stateObj.sitecore, data);
        return Object.freeze({
          title: data.header,
          message: data.body,
          footer: data.footer,
          action: {
            text: data.cta_text,
            url: ctaUrl,
            native: ctaUrl
          },
          allow_close: data.allow_close,
          cta_show: data.cta_show
        });
      };

      return new Promise(resolve => {
        axios
          .get(
            `${api}/v2/CRM.svc/BonusMessage/res_106_cf?label=${portal}&language=${langCode}`
          )
          .then(errorRes => {
            if (errorRes && errorRes !== undefined) {
              notification = buildNotification(errorRes.data.data);
            }
            resolve(notification);
            dataLayer.push({
              event: "Event.Functionality.",
              "page.referringAction": "coin-flip_PlayMoneyPlayer_Error"
            });
          });
      });
    },

    loadLabels(context) {
      const stateObj = context.state;
      const host = stateObj.config.host;
      const portal = stateObj.sitecore.portal.replace(/\./g, "");
      const langCode = stateObj.sitecore.langCode;

      return new Promise(resolve => {
        axios
          .get(`${host}/dist/js/data/labels/${portal}/${langCode}.json`)
          .then(response => {
            context.commit("setLabels", response.data[0]);
            const viewData = response.data[0].views;
            const viewKeys = Object.keys(viewData);
            viewKeys.map((vKey, index) => {
              context.commit("setView", {
                name: vKey,
                id: index,
                ...viewData[vKey]
              });
              return true;
            });

            resolve();
          })
          .catch(error => {
            console.error(error);
          });
      });
    },

    loadUser(context) {
      const stateObj = context.state;
      const api = `${stateObj.config.api}`;
      const user = context.state.user;
      const sitecore = context.state.sitecore;
      return new Promise((resolve, reject) => {
        axios
          .get(
            `${api}/v2/CRM.svc/BonusPromotion/${sitecore.bonusID}/User/${
              user.customerId
            }`
          )
          .then(response => {
            const tgID = response.data.data.targetgroup_id || "core";
            context.commit("setTargetGroup", tgID);
            resolve();
          })
          .catch(error => {
            console.error(error);
            reject();
          });
      }).catch(error => {
        console.error(error);
      });
    },

    loadGame(context) {
      const stateObj = context.state;
      const api = stateObj.config.api;
      const userID = stateObj.user.customerId;
      const portal = stateObj.sitecore.portal;
      const langCode = stateObj.sitecore.langCode;
      const bonusId = stateObj.sitecore.bonusID;

      let userStatus = null;
      return new Promise(resolve => {
        axios
          .get(
            `${api}/v1/sports/flipcoin/user/get?label=${portal}&lang=${langCode}&userid=${userID}&bonus_promotion_gid=${bonusId}`,
            { headers: { "content-type": "application/json" } }
          )
          .then(response => {
            userStatus = response.data;

            if (userStatus.data !== null) {
              context.commit("setRaffleEntries", userStatus.data.user.raffle);
            }
            if (
              userStatus.data !== null &&
              userStatus.data.user.flipToday !== null
            ) {
              context.commit("setAlreadyPlayed");
            }
            dataLayer.push({
              event: "Event.Functionality.",
              "page.referringAction": "coin-flip_GamePage_Shown"
            });

            resolve();
          })
          .catch(error => {
            console.error(error);
          });
      });
    }
  }
});

export default store;



// WEBPACK FOOTER //
// ./app/store/store.js